/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  FocusEvent,
  FocusEventHandler,
  KeyboardEvent,
  KeyboardEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ActionMeta, MultiValue } from 'react-select';
import classNames from 'classnames';
import CreatableSelect from 'react-select/creatable';

import './ReactSelect.scss';


type MultiSelectInputProps = {
  validate: (value: string) => ValidationMessage;
  onChangeHandler: (value: MultiValue<MultiSelectOption>) => void;
  currentValue?: MultiSelectOption[];
  placeholder?: string;
  className?: string;
  label?: string | JSX.Element;
  labelClassName?: string;
  commonErrorMessage?: string;
  idContainer?: string;
  onInputChange?: (inputValue: string) => void;
};

export type MultiSelectOption = {
  label: string;
  value: string;
};

export type ValidationMessage = {
  isValid: boolean;
  errorMessage: string;
};

const createOption = (label: string): MultiSelectOption => ({
  label,
  value: label,
});

const clearActions = new Set(['remove-value', 'pop-value', 'clear']);

/**
 * This component wraps react-select/creatable component, which enable users to create, validate ,remove options with Scalyr styles
 *
 * Required:
 *
 * @param {function} validate        functions that checks whether input is valid or not, and returns a boolean as a result
 * @param {function} onChangeHandler Function is called when the value of the input changes
 *
 * Optional:
 *
 * @param {object}   currentValue          Current input value
 * @param {string}   placeholder           Text to show when the input is empty
 * @param {string}   classname             Additional classnames for the input
 * @param {string}   label                 Label for the text input
 * @param {string}   labelClassName        Additional classnames for the label
 * @param {string}   commonErrorMessage    Additional errorMessage for the form events
 * @param {string}   idContainer           Container Id for testing purposes
 * @returns {Component}
 */

export const MultiSelectInput = ({
  currentValue,
  validate,
  onChangeHandler,
  className,
  placeholder,
  label,
  labelClassName = '',
  commonErrorMessage,
  idContainer,
  onInputChange,
}: MultiSelectInputProps): JSX.Element => {
  const [inputValue, setInputValue] = useState('');
  const [validationErrorMessage, setValidationErrorMessage] = useState('');
  const [value, setValue] = useState<MultiValue<MultiSelectOption>>(currentValue || []);

  useEffect(() => {
    setValue(currentValue || []);
  }, [currentValue]);

  useEffect(() => {
    onInputChange?.(inputValue);
  }, [inputValue, onInputChange]);

  const lastChangeRef = useRef({ prevValue: currentValue, lastAction: '' });

  const handleChange = (val: MultiValue<MultiSelectOption>, { action }: ActionMeta<MultiSelectOption>) => {
    setValidationErrorMessage('');
    lastChangeRef.current = {
      lastAction: action,
      prevValue: currentValue,
    };
    if (clearActions.has(action)) {
      onChangeHandler(val ?? []);
    } else {
      setValue(val ?? []);
    }
  };

  const handleInputChange = (input: string, { action }: { action: string }): void => {
    if (action !== 'input-blur' && action !== 'menu-close') {
      setInputValue(input);
      if (input === '') {
        validate(input);
      }
    } else if (inputValue !== '') {
      const validationData = validate(inputValue);
      if (validationData.isValid) {
        setInputValue('');
        const newValue = [...value, createOption(inputValue)];
        setValue(newValue);
        onChangeHandler(newValue);
      } else {
        setValidationErrorMessage(validationData.errorMessage);
      }
    }
  };

  const handleBlur: FocusEventHandler = (event: FocusEvent) => {
    const input = event.target as HTMLInputElement;
    setInputValue(input.value);
  };

  const handleKeyDown: KeyboardEventHandler = (event: KeyboardEvent) => {
    if (
      clearActions.has(lastChangeRef.current.lastAction) && lastChangeRef.current.prevValue &&
      event.key === 'z' &&
      (event.metaKey || event.ctrlKey)
    ) {
      onChangeHandler(lastChangeRef.current.prevValue);
      event.preventDefault();
      return;
    }

    if (!inputValue) return;

    switch (event.key) {
      case 'Enter':
      case 'Tab':
        const validationData = validate(inputValue);
        if (validationData.isValid) {
          setInputValue('');
          const newValue = [...value, createOption(inputValue)];
          setValue(newValue);
          onChangeHandler(newValue);
        } else {
          setValidationErrorMessage(validationData.errorMessage);
        }
        event.preventDefault();
        break;
      default:
        setValidationErrorMessage('');
        break;
    }
  };

  const errorMessage = (): string => {
    if (validationErrorMessage) return validationErrorMessage;
    if (commonErrorMessage) return commonErrorMessage;
    return '';
  };

  const invalidClassName = !!validationErrorMessage || !!commonErrorMessage ? 'ReactSelect--invalid' : '';

  return (
    <>
      {label && <label className={labelClassName}>{label}</label>}
      <CreatableSelect
        components={{
          DropdownIndicator: null,
        }}
        inputValue={inputValue}
        isClearable
        isMulti
        menuIsOpen={false}
        onBlur={handleBlur}
        onChange={handleChange}
        onInputChange={handleInputChange}
        onKeyDown={handleKeyDown}
        isValidNewOption={(): boolean => false}
        placeholder={placeholder}
        value={value}
        className={classNames([className, invalidClassName, 'ReactSelect'])}
        classNamePrefix="CustomSelect"
        id={idContainer}
      />
      {!!invalidClassName && (
        <p className="ReactSelect__ErrorMessage">
          {errorMessage()}
        </p>
      )}
    </>
  );
};
